import React, { useMemo, useState } from 'react';
import { createTokenPurchaseCheckout } from '../services/tokenPurchaseClient';

const tokenPackages = [
  {
    id: 'p500',
    name: 'Starter',
    tokens: 500,
    price: '$4.99',
    accent: '#10b981',
    description: 'A focused launch for trying a new campaign idea.',
    valueNote: 'Best for first campaigns'
  },
  {
    id: 'p1000',
    name: 'Growth',
    tokens: 1000,
    price: '$8.99',
    accent: '#2563eb',
    description: 'More discovery time for creators building momentum.',
    valueNote: 'Best value under $10',
    savingsNote: 'Save $0.99 compared with two Starter packages',
    popular: true
  },
  {
    id: 'p2500',
    name: 'Momentum',
    tokens: 2500,
    price: '$19.99',
    accent: '#7c3aed',
    description: 'Room to launch multiple campaigns and keep testing.',
    valueNote: 'Lowest cost per token'
  }
];

function formatTokens(tokens) {
  return tokens.toLocaleString();
}

function getDollars(price) {
  return Number(price.replace('$', ''));
}

function BuyTokens({
  userTokens,
  userProfile,
  onNavigateToHome,
  onNavigateToPostLink,
  checkoutRedirect = (url) => window.location.assign(url)
}) {
  const [loading, setLoading] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState('p1000');
  const [statusMessage, setStatusMessage] = useState(null);

  const selectedPackage = useMemo(
    () => tokenPackages.find((pkg) => pkg.id === selectedPackageId) || tokenPackages[1],
    [selectedPackageId]
  );

  const selectedCostPerThousand = useMemo(
    () => (getDollars(selectedPackage.price) / selectedPackage.tokens * 1000).toFixed(2),
    [selectedPackage]
  );

  const handlePurchase = async (pkg) => {
    if (loading) return;

    setSelectedPackageId(pkg.id);

    if (!userProfile?.uid) {
      setStatusMessage({
        type: 'warning',
        title: 'Sign in to buy tokens',
        body: 'Log in first so any future purchase can be tied to the right PromoGrind account.'
      });
      return;
    }

    setLoading(pkg.id);
    setStatusMessage({
      type: 'info',
      title: 'Starting secure checkout',
      body: 'PromoGrind is preparing the selected package. You will review payment details in Stripe before any charge.'
    });

    try {
      const checkout = await createTokenPurchaseCheckout(pkg.id);
      if (!checkout?.url) {
        throw new Error('Checkout URL was not returned.');
      }

      checkoutRedirect(checkout.url);
    } catch (err) {
      console.error(err);
      setStatusMessage({
        type: 'error',
        title: 'Checkout is unavailable',
        body: 'We could not start checkout from this environment. Your token balance has not changed.'
      });
    } finally {
      setLoading(null);
    }
  };

  const handlePackageKeyDown = (event, pkg) => {
    const currentIndex = tokenPackages.findIndex((item) => item.id === selectedPackageId);

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelectedPackageId(pkg.id);
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      const nextPackage = tokenPackages[(currentIndex + 1) % tokenPackages.length];
      setSelectedPackageId(nextPackage.id);
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      const previousPackage =
        tokenPackages[(currentIndex - 1 + tokenPackages.length) % tokenPackages.length];
      setSelectedPackageId(previousPackage.id);
    }
  };

  const visibleNotice = statusMessage;

  return (
    <div style={pageStyle}>
      <style>{responsiveStyles}</style>

      <section className="buy-tokens-hero" style={heroStyle}>
        <div style={heroCopyStyle}>
          <p style={eyebrowStyle}>Creator campaign fuel</p>
          <h1 style={headingStyle}>Buy PromoGrind Tokens</h1>
          <p style={subheadingStyle}>
            Tokens will be used to launch creator campaigns and invite internal discovery,
            feedback, and audience signal inside PromoGrind.
          </p>
        </div>

        <div style={balancePanelStyle} aria-label="Current token balance">
          <span style={balanceLabelStyle}>Current balance</span>
          <strong style={balanceAmountStyle}>{formatTokens(userTokens || 0)}</strong>
          <span style={balanceUnitStyle}>tokens</span>
          {userProfile && (
            <span style={earnedStyle}>
              Total earned: {formatTokens(userProfile.totalEarned || 0)}
            </span>
          )}
        </div>
      </section>

      {visibleNotice && (
        <div style={noticeStyle(visibleNotice.type)} role="status" aria-live="polite">
          <strong style={noticeTitleStyle}>{visibleNotice.title}</strong>
          <span>{visibleNotice.body}</span>
        </div>
      )}

      <section className="buy-tokens-summary" style={selectionSummaryStyle}>
        <div>
          <p style={sectionLabelStyle}>Selected package</p>
          <h2 style={summaryTitleStyle}>
            {selectedPackage.name}: {formatTokens(selectedPackage.tokens)} tokens
          </h2>
        </div>
        <div style={summaryValueStyle}>
          <span>{selectedPackage.price}</span>
          <small>${selectedCostPerThousand} per 1,000 tokens</small>
        </div>
      </section>

      <section
        style={packageGridStyle}
        role="radiogroup"
        aria-label="Choose a token package"
      >
        {tokenPackages.map((pkg) => {
          const selected = pkg.id === selectedPackageId;
          const costPerThousand = (getDollars(pkg.price) / pkg.tokens * 1000).toFixed(2);
          const isLoading = loading === pkg.id;
          const disabled = Boolean(loading);

          return (
            <article
              className="buy-token-card"
              key={pkg.id}
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => setSelectedPackageId(pkg.id)}
              onKeyDown={(event) => handlePackageKeyDown(event, pkg)}
              style={cardStyle(pkg, selected)}
            >
              {pkg.popular && <span style={popularBadgeStyle}>Most Popular</span>}

              <div style={cardTopStyle}>
                <span style={packageNameStyle}>{pkg.name}</span>
                <span style={radioStyle(pkg, selected)} aria-hidden="true">
                  {selected ? 'Selected' : 'Choose'}
                </span>
              </div>

              <div style={tokenAmountStyle}>
                {formatTokens(pkg.tokens)}
                <span>tokens</span>
              </div>

              <p style={descriptionStyle}>{pkg.description}</p>

              <div style={valueRowsStyle}>
                <div style={valueRowStyle}>
                  <span>Package price</span>
                  <strong>{pkg.price}</strong>
                </div>
                <div style={valueRowStyle}>
                  <span>Cost per 1,000</span>
                  <strong>${costPerThousand}</strong>
                </div>
                <div style={highlightRowStyle(pkg)}>
                  <span>{pkg.valueNote}</span>
                </div>
                {pkg.savingsNote && (
                  <div style={savingsRowStyle}>
                    <span>{pkg.savingsNote}</span>
                  </div>
                )}
              </div>

              <button
                className="buy-token-button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handlePurchase(pkg);
                }}
                disabled={disabled}
                style={buttonStyle(pkg, disabled)}
              >
                {isLoading ? 'Preparing checkout...' : `Choose ${pkg.name}`}
              </button>
            </article>
          );
        })}
      </section>

      <section id="checkout-preview-note" className="buy-tokens-trust" style={trustPanelStyle}>
        <div>
          <h3 style={trustHeadingStyle}>Secure checkout path</h3>
          <p style={trustCopyStyle}>
            Payment details are handled by Stripe. PromoGrind credits tokens only after
            the trusted server verifies the payment and records the final purchase.
          </p>
        </div>
        <div style={trustPillsStyle}>
          <span style={trustPillStyle}>Stripe-hosted checkout</span>
          <span style={trustPillStyle}>No card storage in app</span>
          <span style={trustPillStyle}>Server-verified crediting</span>
        </div>
      </section>

      <nav style={navStyle} aria-label="Buy tokens navigation">
        <button type="button" onClick={onNavigateToPostLink} style={secondaryButtonStyle}>
          Create Campaign
        </button>
        <button type="button" onClick={onNavigateToHome} style={tertiaryButtonStyle}>
          Discovery Dashboard
        </button>
      </nav>

      <p style={supportStyle}>Questions about payments? Contact support@promogrind.com.</p>
    </div>
  );
}

const pageStyle = {
  minHeight: 'calc(100vh - 76px)',
  padding: '32px 16px 42px',
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef6f0 42%, #f8fafc 100%)',
  color: '#1f2937'
};

const heroStyle = {
  maxWidth: 1120,
  margin: '0 auto 22px',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(230px, 320px)',
  gap: 20,
  alignItems: 'stretch'
};

const heroCopyStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #d9e7dd',
  borderRadius: 8,
  padding: 26,
  boxShadow: '0 14px 36px rgba(15, 23, 42, 0.08)'
};

const eyebrowStyle = {
  margin: '0 0 10px',
  color: '#047857',
  fontSize: 13,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0
};

const headingStyle = {
  margin: '0 0 12px',
  color: '#111827',
  fontSize: 34,
  lineHeight: 1.12,
  letterSpacing: 0
};

const subheadingStyle = {
  margin: 0,
  maxWidth: 720,
  color: '#4b5563',
  fontSize: 17,
  lineHeight: 1.55
};

const balancePanelStyle = {
  background: 'linear-gradient(135deg, #0f766e 0%, #2563eb 100%)',
  color: '#ffffff',
  borderRadius: 8,
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: '0 16px 34px rgba(15, 118, 110, 0.24)'
};

const balanceLabelStyle = {
  fontSize: 14,
  fontWeight: 700,
  opacity: 0.9
};

const balanceAmountStyle = {
  fontSize: 42,
  lineHeight: 1,
  marginTop: 10
};

const balanceUnitStyle = {
  fontSize: 18,
  marginTop: 4
};

const earnedStyle = {
  marginTop: 14,
  paddingTop: 14,
  borderTop: '1px solid rgba(255,255,255,0.32)',
  fontSize: 14,
  opacity: 0.92
};

const sectionLabelStyle = {
  margin: '0 0 4px',
  color: '#64748b',
  fontSize: 13,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0
};

const selectionSummaryStyle = {
  maxWidth: 1120,
  margin: '0 auto 18px',
  backgroundColor: '#ffffff',
  border: '1px solid #dbe4ee',
  borderRadius: 8,
  padding: '16px 18px',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 18,
  alignItems: 'center'
};

const summaryTitleStyle = {
  margin: 0,
  color: '#111827',
  fontSize: 20
};

const summaryValueStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  color: '#111827',
  fontWeight: 800,
  fontSize: 22
};

const packageGridStyle = {
  maxWidth: 1120,
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 18
};

const cardStyle = (pkg, selected) => ({
  position: 'relative',
  backgroundColor: '#ffffff',
  border: selected ? `3px solid ${pkg.accent}` : '1px solid #d8e0ea',
  borderRadius: 8,
  padding: selected ? 22 : 24,
  boxShadow: selected
    ? `0 18px 40px ${pkg.accent}26`
    : '0 10px 26px rgba(15, 23, 42, 0.08)',
  cursor: 'pointer',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 390,
  transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease'
});

const popularBadgeStyle = {
  position: 'absolute',
  top: -13,
  left: 18,
  backgroundColor: '#111827',
  color: '#ffffff',
  padding: '6px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800
};

const cardTopStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginBottom: 24,
  paddingTop: 6
};

const packageNameStyle = {
  color: '#111827',
  fontSize: 22,
  fontWeight: 800
};

const radioStyle = (pkg, selected) => ({
  border: `1px solid ${selected ? pkg.accent : '#cbd5e1'}`,
  backgroundColor: selected ? `${pkg.accent}14` : '#f8fafc',
  color: selected ? pkg.accent : '#64748b',
  borderRadius: 999,
  padding: '7px 10px',
  fontSize: 12,
  fontWeight: 800
});

const tokenAmountStyle = {
  color: '#111827',
  fontSize: 40,
  fontWeight: 900,
  lineHeight: 1,
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 14
};

const descriptionStyle = {
  color: '#526070',
  lineHeight: 1.45,
  minHeight: 44,
  margin: '0 0 18px'
};

const valueRowsStyle = {
  display: 'grid',
  gap: 9,
  marginBottom: 20
};

const valueRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 10,
  padding: '11px 12px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e5edf5',
  borderRadius: 8,
  color: '#475569'
};

const highlightRowStyle = (pkg) => ({
  padding: '11px 12px',
  borderRadius: 8,
  backgroundColor: `${pkg.accent}12`,
  color: pkg.accent,
  fontWeight: 800
});

const savingsRowStyle = {
  padding: '10px 12px',
  borderRadius: 8,
  backgroundColor: '#fffbeb',
  color: '#92400e',
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1.35
};

const buttonStyle = (pkg, disabled) => ({
  marginTop: 'auto',
  width: '100%',
  border: 'none',
  borderRadius: 8,
  padding: '14px 16px',
  backgroundColor: disabled ? '#94a3b8' : pkg.accent,
  color: '#ffffff',
  fontSize: 16,
  fontWeight: 800,
  cursor: disabled ? 'not-allowed' : 'pointer'
});

const noticeStyle = (type) => {
  const variants = {
    info: { backgroundColor: '#eff6ff', borderColor: '#93c5fd', color: '#1e3a8a' },
    warning: { backgroundColor: '#fffbeb', borderColor: '#f59e0b', color: '#92400e' },
    error: { backgroundColor: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b' }
  };

  return {
    maxWidth: 1120,
    margin: '0 auto 18px',
    padding: '14px 16px',
    border: `1px solid ${(variants[type] || variants.info).borderColor}`,
    borderRadius: 8,
    backgroundColor: (variants[type] || variants.info).backgroundColor,
    color: (variants[type] || variants.info).color,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    lineHeight: 1.4
  };
};

const noticeTitleStyle = {
  fontSize: 15
};

const trustPanelStyle = {
  maxWidth: 1120,
  margin: '20px auto 0',
  backgroundColor: '#ffffff',
  border: '1px solid #dbe4ee',
  borderRadius: 8,
  padding: 20,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(240px, 360px)',
  gap: 18,
  alignItems: 'center'
};

const trustHeadingStyle = {
  margin: '0 0 8px',
  color: '#111827',
  fontSize: 20
};

const trustCopyStyle = {
  margin: 0,
  color: '#526070',
  lineHeight: 1.5
};

const trustPillsStyle = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  justifyContent: 'flex-end'
};

const trustPillStyle = {
  backgroundColor: '#f1f5f9',
  border: '1px solid #dbe4ee',
  borderRadius: 999,
  padding: '8px 10px',
  color: '#334155',
  fontSize: 13,
  fontWeight: 700
};

const navStyle = {
  maxWidth: 1120,
  margin: '24px auto 0',
  display: 'flex',
  justifyContent: 'center',
  gap: 12,
  flexWrap: 'wrap'
};

const secondaryButtonStyle = {
  backgroundColor: '#047857',
  color: '#ffffff',
  border: 'none',
  borderRadius: 8,
  padding: '12px 20px',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 800
};

const tertiaryButtonStyle = {
  backgroundColor: '#ffffff',
  color: '#2563eb',
  border: '1px solid #bfdbfe',
  borderRadius: 8,
  padding: '12px 20px',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 800
};

const supportStyle = {
  margin: '22px auto 0',
  maxWidth: 1120,
  textAlign: 'center',
  color: '#64748b',
  fontSize: 14
};

const responsiveStyles = `
  .buy-token-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.14) !important;
  }

  .buy-token-card:focus-visible,
  .buy-token-button:focus-visible {
    outline: 3px solid #f59e0b !important;
    outline-offset: 3px;
  }

  .buy-token-button:not(:disabled):hover {
    filter: brightness(0.95);
  }

  @media (max-width: 760px) {
    .buy-tokens-hero,
    .buy-tokens-trust {
      grid-template-columns: 1fr !important;
    }

    .buy-tokens-summary {
      align-items: flex-start !important;
      flex-direction: column !important;
    }
  }

  @media (max-width: 520px) {
    .buy-tokens-hero h1 {
      font-size: 28px !important;
    }

    .buy-tokens-summary > div:last-child {
      align-items: flex-start !important;
    }
  }
`;

export default BuyTokens;
